import {getTestProps} from '@benchling/js-test-utils';
import {Box, Flex, Text, TextInput, createStyles} from '@mantine/core';
import React, {useMemo, useRef} from 'react';

import {
  MODULE_COLOR,
  CENTER_COLOR,
  CHAIN_SEPARATOR_COLOR,
  CENTER_BACKGROUND_COLOR,
  MODULE_BACKGROUND_COLOR,
  SYMBOL_COLOR,
} from 'antibodies/format/VeritasSyntaxKey';
import {tokenizeVeritasName, VeritasToken} from 'antibodies/format/veritasTokenizer';
import {FONT_FAMILY_MONOSPACED, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';

interface VeritasHighlightedInputProps {
  id?: string;
  value: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
}

const INPUT_HEIGHT_PX = 36;
const INPUT_PADDING_X_PX = 14;
const INPUT_FONT_SIZE_PX = 14;
const INPUT_FONT_FAMILY = FONT_FAMILY_MONOSPACED;
const LETTER_SPACING_PX = 2;

const useInputStyles = createStyles((theme) => ({
  input: {
    color: 'transparent',
    caretColor: 'black',
    backgroundColor: 'transparent',
    height: `${INPUT_HEIGHT_PX}px`,
    paddingLeft: `${INPUT_PADDING_X_PX}px`,
    paddingRight: `${INPUT_PADDING_X_PX}px`,
    fontFamily: INPUT_FONT_FAMILY,
    fontSize: `${INPUT_FONT_SIZE_PX}px`,
    letterSpacing: `${LETTER_SPACING_PX}px`,
    '&::placeholder': {
      letterSpacing: 'normal',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  },
}));

const useOverlayStyles = createStyles((theme) => ({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    height: `${INPUT_HEIGHT_PX}px`,
    paddingLeft: `${INPUT_PADDING_X_PX}px`,
    paddingRight: `${INPUT_PADDING_X_PX}px`,
    whiteSpace: 'pre',
    overflowX: 'hidden',
  },
}));

const TOKEN_BASE = {
  fontFamily: INPUT_FONT_FAMILY,
  fontSize: `${INPUT_FONT_SIZE_PX}px`,
  letterSpacing: `${LETTER_SPACING_PX}px`,
} as const;

const OUTLINED_TOKEN_BASE = {
  ...TOKEN_BASE,
  position: 'relative',
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `-2px`,
    right: `-1px`, // Less on the right to account for the letter spacing
    borderRadius: 2,
    backgroundColor: 'inherit',
    pointerEvents: 'none',
    zIndex: -1,
  },
} as const;

const useTokenStyles = createStyles((theme, {token}: {token: VeritasToken}) => {
  if (token.type === 'symbol') {
    if (token.value === '*') {
      return {
        root: {
          ...TOKEN_BASE,
          color: CHAIN_SEPARATOR_COLOR,
          fontWeight: 800,
        },
      };
    }
    return {
      root: {
        ...TOKEN_BASE,
        color: SYMBOL_COLOR,
      },
    };
  } else if (token.type === 'center') {
    return {
      root: {
        ...OUTLINED_TOKEN_BASE,
        color: CENTER_COLOR,
        backgroundColor: CENTER_BACKGROUND_COLOR,
        fontFamily: FONT_FAMILY_MONOSPACED,
        fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
        textDecoration: 'underline',
      },
    };
  } else if (token.type === 'module') {
    return {
      root: {
        ...OUTLINED_TOKEN_BASE,
        color: MODULE_COLOR,
        backgroundColor: MODULE_BACKGROUND_COLOR,
        fontFamily: FONT_FAMILY_MONOSPACED,
        fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
      },
    };
  } else {
    return {
      root: {
        ...TOKEN_BASE,
        color: 'inherit',
        fontFamily: FONT_FAMILY_MONOSPACED,
      },
    };
  }
});

function Token({token, fontSize}: {token: VeritasToken; fontSize?: string}): JSX.Element {
  const {classes: tokenClasses} = useTokenStyles({token});
  return (
    <Text className={tokenClasses.root} span={true} fz={fontSize}>
      {token.value}
    </Text>
  );
}

/**
 * An input field with special syntax highlighting for VERITAS names. The input component itself is
 * transparent, and the specially formatted spans are rendered above it, staying in sync with the cursor.
 *
 * @param id - The id of the input field.
 * @param value - The value of the input field.
 * @param onChange - The function to call when the value of the input field changes.
 * @param onKeyDown - The function to call when a key is pressed.
 * @param onBlur - The function to call when the input field loses focus.
 * @param placeholder - The placeholder text to display in the input field.
 * @param readOnly - Whether the input field is read only.
 * @param disabled - Whether the input field is disabled.
 */
function VeritasHighlightedInput({
  id,
  value,
  onChange,
  onKeyDown,
  onBlur,
  placeholder,
  readOnly,
  disabled,
}: VeritasHighlightedInputProps): JSX.Element {
  const {classes: inputClasses} = useInputStyles();
  const {classes: overlayClasses} = useOverlayStyles();
  const backdropRef = useRef<HTMLDivElement>(null);

  const tokens = useMemo(() => tokenizeVeritasName(value), [value]);

  const handleScroll = (e: React.UIEvent<HTMLInputElement>): void => {
    if (backdropRef.current) {
      backdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  return (
    <Box pos="relative" w="100%">
      <Flex
        className={overlayClasses.root}
        ref={backdropRef}
        align="center"
        aria-hidden="true"
        {...getTestProps({component: 'VeritasHighlightedInput', element: 'backdrop'})}
      >
        {tokens.map((token, i) => (
          <Token key={i} token={token} />
        ))}
      </Flex>
      <TextInput
        id={id}
        type="text"
        value={value}
        onChange={
          onChange
            ? (e) => {
                onChange(e.target.value);
              }
            : undefined
        }
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onScroll={handleScroll}
        placeholder={placeholder}
        autoComplete="off"
        readOnly={readOnly}
        disabled={disabled}
        classNames={inputClasses}
      />
    </Box>
  );
}

/** Renders a VERITAS name as styled inline tokens, without an input field. */
export function VeritasHighlightedText({
  value,
  fontSize,
  forceModule,
}: {
  value: string;
  fontSize?: string;
  forceModule?: boolean;
}): JSX.Element {
  const tokens = useMemo(() => tokenizeVeritasName(value, forceModule), [value, forceModule]);
  return (
    <Flex display="inline-flex" align="center" gap={0}>
      {tokens.map((token, i) => (
        <Token key={i} token={token} fontSize={fontSize} />
      ))}
    </Flex>
  );
}

export default VeritasHighlightedInput;
export type {VeritasHighlightedInputProps};
